import {createReducer} from 'redux-act';
import {
  GET_BYOC_ONBOARDING_DATA,
  GET_BYOC_ONBOARDING_DATA_SUCCESS,
  GET_BYOC_ONBOARDING_DATA_ERROR,
  SAVE_BYOC_SIGNUP,
  SAVE_BYOC_SIGNUP_SUCCESS,
  SAVE_BYOC_SIGNUP_ERROR,
  SAVE_BYOC_MENU_SETUP,
  SAVE_BYOC_MENU_SETUP_SUCCESS,
  SAVE_BYOC_MENU_SETUP_ERROR,
  SAVE_BYOC_ORDER_SOURCE_INTEGRATION,
  SAVE_BYOC_ORDER_SOURCE_INTEGRATION_SUCCESS,
  SAVE_BYOC_ORDER_SOURCE_INTEGRATION_ERROR,
  SAVE_BYOC_PAYMENT_DETAILS,
  SAVE_BYOC_PAYMENT_DETAILS_SUCCESS,
  SAVE_BYOC_PAYMENT_DETAILS_ERROR,
  SAVE_BYOC_ADJUSTMENT_PRICE_RULES,
  SAVE_BYOC_ADJUSTMENT_PRICE_RULES_SUCCESS,
  SAVE_BYOC_ADJUSTMENT_PRICE_RULES_ERROR,
  UPDATE_BYOC_IS_CONNECTED_TO_POS,
  UPDATE_BYOC_IS_CONNECTED_TO_POS_SUCCESS,
  UPDATE_BYOC_IS_CONNECTED_TO_POS_ERROR,
  GET_BYOC_AGREEMENT,
  GET_BYOC_AGREEMENT_SUCCESS,
  GET_BYOC_AGREEMENT_ERROR,
  MARK_BYOC_ORDER_SOURCE_FINISHED,
  MARK_BYOC_ORDER_SOURCE_FINISHED_SUCCESS,
  MARK_BYOC_ORDER_SOURCE_FINISHED_ERROR,
  CLEAR_BYOC_ONBOARDING_DATA,
  SAVE_BYOC_STRIPE_FLOW_COMPLETED_SUCCESS,
  UPDATE_BYOC_ONBOARDING_STEP,
  UPDATE_BYOC_ONBOARDING_STEP_SUCCESS,
  UPDATE_BYOC_ONBOARDING_STEP_ERROR,
  SAVE_BYOC_SIGNATURE,
  SAVE_BYOC_SIGNATURE_SUCCESS,
  SAVE_BYOC_SIGNATURE_ERROR,
  UPDATE_BYOC_COMMISSION_STATE
} from './actions';

const initialState = () => ({
  loading: false,
  globalLoading: false,
  error: null,
  data: {
    id: null,
    signup: {
      plan: null,
      integrationFee: null,
      promocode: null,
      termsAgreed: false,
      hasSignature: false,
      isFinished: false
    },
    menuSetup: {
      source: null,
      adjustmentPriceRules: [],
      isFinished: false
    },
    orderSourceIntegration: {
      channel: null,
      isConnectedtoPos: false,
      connectedPlatforms: [],
      selectedPlatforms: [],
      isFinished: false,
      agreementInfo: {
        connectedPlatforms: [],
        isAgreementPageGenerated: false,
        agreementPageUrl: null
      }
    },
    paymentDetails: {
      stripeCustomerId: null,
      paymentMethodConnected: false,
      isFinished: false
    },
    createdAt: null,
    updatedAt: null
  },
  lastStep: null // Track last completed step
});

// Helper function to determine last completed step
// Returns the next step that should be shown (not the completed one)
function getLastStep(data) {
  // Normalize data keys (handle both camelCase and PascalCase)
  const signup = data?.signup || data?.Signup || {};
  const menuSetup = data?.menuSetup || data?.MenuSetup || {};
  const orderSourceIntegration =
    data?.orderSourceIntegration || data?.OrderSourceIntegration || {};
  const paymentDetails = data?.paymentDetails || data?.PaymentDetails || {};

  // If all steps are finished, show integration status
  if (paymentDetails?.isFinished || paymentDetails?.IsFinished) {
    return 'integration-status';
  }
  // If OrderSourceIntegration is finished, show payment details
  if (
    orderSourceIntegration?.isFinished ||
    orderSourceIntegration?.IsFinished
  ) {
    return 'payment-details';
  }
  // If menuSetup is finished, show order source integration
  if (menuSetup?.isFinished || menuSetup?.IsFinished) {
    return 'order-source-integration';
  }
  // If Signup is finished, show sign-up-success page
  // IMPORTANT: Check signup before menuSetup, because if signup is finished,
  // we should show success page even if menuSetup has started
  if (signup?.isFinished || signup?.IsFinished) {
    return 'sign-up-success';
  }
  // If menuSetup has source (step is started but not finished), show menu-setup page
  if (menuSetup?.source || menuSetup?.Source) {
    return 'menu-setup';
  }
  // If Signup has plan (step is started but not finished), show sign-up page
  if (signup?.plan || signup?.Plan) {
    return 'sign-up';
  }
  // Default: show start page
  return 'start';
}

const byocOnboardingReducer = createReducer(
  {
    [GET_BYOC_ONBOARDING_DATA]: (state) => ({
      ...state,
      loading: true,
      globalLoading: true,
      error: null
    }),
    [GET_BYOC_ONBOARDING_DATA_SUCCESS]: (state, payload) => {
      // Handle both formats: nested data or flat data, camelCase and PascalCase
      const responseData = payload?.data || payload || {};
      const data = {
        id: responseData.id || state.data.id,
        signup: responseData.signup || responseData.Signup || state.data.signup,
        menuSetup:
          responseData.menuSetup ||
          responseData.MenuSetup ||
          state.data.menuSetup,
        orderSourceIntegration:
          responseData.orderSourceIntegration ||
          responseData.OrderSourceIntegration ||
          state.data.orderSourceIntegration,
        paymentDetails:
          responseData.paymentDetails ||
          responseData.PaymentDetails ||
          state.data.paymentDetails,
        createdAt: responseData.createdAt || state.data.createdAt,
        updatedAt: responseData.updatedAt || state.data.updatedAt
      };
      return {
        ...state,
        data,
        loading: false,
        globalLoading: false,
        error: null,
        lastStep: getLastStep(data)
      };
    },
    [GET_BYOC_ONBOARDING_DATA_ERROR]: (state, error) => ({
      ...state,
      loading: false,
      globalLoading: false,
      error,
      lastStep: 'start' // Set to start page on error to allow flow to continue
    }),
    [SAVE_BYOC_SIGNUP]: (state) => ({
      ...state,
      loading: true
    }),
    [SAVE_BYOC_SIGNUP_SUCCESS]: (state, payload) => {
      // Handle both formats: { Signup: {...} } or direct {...}, camelCase and PascalCase
      const signupData = payload?.signup || payload?.Signup || payload || {};
      const updatedData = {
        ...state.data,
        signup: {
          ...state.data.signup,
          ...signupData
        }
      };
      return {
        ...state,
        loading: false,
        error: null,
        data: updatedData,
        lastStep: getLastStep(updatedData)
      };
    },
    [SAVE_BYOC_SIGNUP_ERROR]: (state, error) => ({
      ...state,
      loading: false
    }),
    [SAVE_BYOC_MENU_SETUP]: (state) => ({
      ...state,
      loading: true
    }),
    [SAVE_BYOC_MENU_SETUP_SUCCESS]: (state, payload) => {
      const menuSetupData =
        payload?.menuSetup || payload?.MenuSetup || payload || {};
      return {
        ...state,
        loading: false,
        error: null,
        data: {
          ...state.data,
          menuSetup: {
            ...state.data.menuSetup,
            ...menuSetupData
          }
        },
        lastStep: getLastStep({
          ...state.data,
          menuSetup: {
            ...state.data.menuSetup,
            ...menuSetupData
          }
        })
      };
    },
    [SAVE_BYOC_MENU_SETUP_ERROR]: (state, error) => ({
      ...state,
      loading: false
    }),
    [SAVE_BYOC_ORDER_SOURCE_INTEGRATION]: (state) => ({
      ...state,
      loading: true
    }),
    [SAVE_BYOC_ORDER_SOURCE_INTEGRATION_SUCCESS]: (state, payload) => {
      const orderSourceData =
        payload?.orderSourceIntegration ||
        payload?.OrderSourceIntegration ||
        payload ||
        {};
      return {
        ...state,
        loading: false,
        error: null,
        data: {
          ...state.data,
          orderSourceIntegration: {
            ...state.data.orderSourceIntegration,
            ...orderSourceData
          }
        },
        lastStep: getLastStep({
          ...state.data,
          orderSourceIntegration: {
            ...state.data.orderSourceIntegration,
            ...orderSourceData
          }
        })
      };
    },
    [SAVE_BYOC_ORDER_SOURCE_INTEGRATION_ERROR]: (state, error) => ({
      ...state,
      loading: false
    }),
    [SAVE_BYOC_PAYMENT_DETAILS]: (state) => ({
      ...state,
      loading: true
    }),
    [SAVE_BYOC_PAYMENT_DETAILS_SUCCESS]: (state, payload) => {
      const paymentDetailsData =
        payload?.paymentDetails || payload?.PaymentDetails || payload || {};
      return {
        ...state,
        loading: false,
        error: null,
        data: {
          ...state.data,
          paymentDetails: {
            ...state.data.paymentDetails,
            ...paymentDetailsData
          }
        },
        lastStep: getLastStep({
          ...state.data,
          paymentDetails: {
            ...state.data.paymentDetails,
            ...paymentDetailsData
          }
        })
      };
    },
    [SAVE_BYOC_PAYMENT_DETAILS_ERROR]: (state, error) => ({
      ...state,
      loading: false
    }),
    [SAVE_BYOC_STRIPE_FLOW_COMPLETED_SUCCESS]: (state) => ({
      ...state,
      data: {
        ...state.data,
        paymentDetails: {
          ...state.data.paymentDetails,
          paymentMethodConnected: true
        }
      }
    }),
    [SAVE_BYOC_ADJUSTMENT_PRICE_RULES]: (state) => ({
      ...state,
      loading: true
    }),
    [SAVE_BYOC_ADJUSTMENT_PRICE_RULES_SUCCESS]: (state, payload) => {
      // Update only adjustmentPriceRules in menuSetup
      // Handle both formats: { adjustmentPriceRules: [...] } or direct array
      const rules =
        payload?.adjustmentPriceRules ||
        payload?.AdjustmentPriceRules ||
        (Array.isArray(payload) ? payload : []);
      return {
        ...state,
        loading: false,
        error: null,
        data: {
          ...state.data,
          menuSetup: {
            ...state.data.menuSetup,
            adjustmentPriceRules: rules,
            areAdjustmentPriceRulesApplied: rules.length > 0
          }
        },
        lastStep: 'menu-setup'
      };
    },
    [SAVE_BYOC_ADJUSTMENT_PRICE_RULES_ERROR]: (state, error) => ({
      ...state,
      loading: false,
      lastStep: 'menu-setup'
    }),
    [UPDATE_BYOC_IS_CONNECTED_TO_POS]: (state) => ({
      ...state,
      loading: true
    }),
    [UPDATE_BYOC_IS_CONNECTED_TO_POS_SUCCESS]: (state, payload) => {
      // Update isConnectedtoPos in orderSourceIntegration
      let isConnected = true; // Default value
      if (payload?.isConnectedtoPos !== undefined) {
        isConnected = payload.isConnectedtoPos;
      } else if (payload?.IsConnectedtoPos !== undefined) {
        isConnected = payload.IsConnectedtoPos;
      } else if (payload?.IsConnectedToPos !== undefined) {
        isConnected = payload.IsConnectedToPos;
      }

      return {
        ...state,
        loading: false,
        error: null,
        data: {
          ...state.data,
          orderSourceIntegration: {
            ...state.data.orderSourceIntegration,
            isConnectedtoPos: isConnected
          }
        }
      };
    },
    [UPDATE_BYOC_IS_CONNECTED_TO_POS_ERROR]: (state, error) => ({
      ...state,
      loading: false
    }),
    [GET_BYOC_AGREEMENT]: (state) => ({
      ...state,
      loading: true
    }),
    [GET_BYOC_AGREEMENT_SUCCESS]: (state) => {
      return {
        ...state,
        loading: false,
        error: null
      };
    },
    [GET_BYOC_AGREEMENT_ERROR]: (state, error) => ({
      ...state,
      loading: false
    }),
    [MARK_BYOC_ORDER_SOURCE_FINISHED]: (state) => ({
      ...state,
      loading: true
    }),
    [MARK_BYOC_ORDER_SOURCE_FINISHED_SUCCESS]: (state) => {
      // Mark orderSourceIntegration as finished
      return {
        ...state,
        loading: false,
        error: null,
        data: {
          ...state.data,
          orderSourceIntegration: {
            ...state.data.orderSourceIntegration,
            isFinished: true
          }
        },
        lastStep: getLastStep({
          ...state.data,
          orderSourceIntegration: {
            ...state.data.orderSourceIntegration,
            isFinished: true
          }
        })
      };
    },
    [UPDATE_BYOC_ONBOARDING_STEP]: (state) => ({
      ...state,
      loading: true
    }),
    [UPDATE_BYOC_ONBOARDING_STEP_SUCCESS]: (state, payload) => {
      // Handle new unified format with camelCase
      const responseData = payload?.data || payload || {};
      const updatedData = {
        ...state.data,
        ...(responseData.id && {id: responseData.id}),
        ...(responseData.signup && {
          signup: {
            ...state.data.signup,
            ...responseData.signup
          }
        }),
        ...(responseData.menuSetup && {
          menuSetup: {
            ...state.data.menuSetup,
            ...responseData.menuSetup
          }
        }),
        ...(responseData.orderSourceIntegration && {
          orderSourceIntegration: {
            ...state.data.orderSourceIntegration,
            ...responseData.orderSourceIntegration
          }
        }),
        ...(responseData.paymentDetails && {
          paymentDetails: {
            ...state.data.paymentDetails,
            ...responseData.paymentDetails
          }
        }),
        ...(responseData.createdAt && {createdAt: responseData.createdAt}),
        ...(responseData.updatedAt && {updatedAt: responseData.updatedAt})
      };
      return {
        ...state,
        loading: false,
        error: null,
        data: updatedData,
        lastStep: getLastStep(updatedData)
      };
    },
    [UPDATE_BYOC_COMMISSION_STATE]: (state, payload) => {
      return {
        ...state,
        data: {
          ...state.data,
          orderSourceIntegration: {
            ...state.data.orderSourceIntegration,
            platformCommissions: payload
          }
        }
      };
    },
    [UPDATE_BYOC_ONBOARDING_STEP_ERROR]: (state, error) => ({
      ...state,
      loading: false,
      error
    }),
    [MARK_BYOC_ORDER_SOURCE_FINISHED_ERROR]: (state, error) => ({
      ...state,
      loading: false
    }),
    [CLEAR_BYOC_ONBOARDING_DATA]: () => initialState()
  },
  initialState()
);

export default byocOnboardingReducer;
